import type React from "react";

import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { HealthReportV2Response } from "api/types/GetHealthReportsV2Response";
import { StyledSelect } from "shared/atoms/MUI/MUISelect";
import { useLocalizedDate } from "utils/date";

interface ReportsDropdownV2Props {
  reportList?: HealthReportV2Response[];
  selected: number[];
  changeCallback: (selectedIds: number[]) => void;
}

export const ReportsDropdownV2: React.FC<ReportsDropdownV2Props> = ({ selected, reportList, changeCallback }) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const { value } = event.target;
    const newSelected = typeof value === "string" ? value.split(",").map(Number) : (value as number[]);

    if (newSelected.length > 0) {
      changeCallback(newSelected);
    }
  };

  const renderValue = (value: unknown) => {
    const selectedIds = Array.isArray(value) ? (value as number[]) : [];
    switch (selectedIds.length) {
      case 0:
        return t("patients.health_reports.none");
      case 1: {
        const selection = reportList?.find(el => el.id === selectedIds[0]);
        if (selection) {
          return `${t("patients.health_reports.q_number_v2", {
            number: selection.week_number,
          })} | ${format(parseISO(selection.created_at), "P")}`;
        }
        return t("patients.health_reports.selected", { number: 1 });
      }
      default:
        return t("patients.health_reports.selected", { number: selectedIds.length });
    }
  };

  return (
    <FormControl fullWidth data-testid="new-reports-dropdown-v2">
      <InputLabel id="reports-v2-select-label" shrink>
        {t("patients.health_reports.questionnaire_label")}
      </InputLabel>
      <StyledSelect
        labelId="reports-v2-select-label"
        id="reports-v2-select"
        multiple
        value={selected}
        onChange={handleChange}
        renderValue={renderValue}
        label={t("patients.health_reports.questionnaire_label")}
        disabled={!reportList?.length}
        data-testid="reports-v2-select"
        size="small"
        displayEmpty
      >
        {reportList?.map(report => {
          const isSelected = selected.includes(report.id);
          const isDisabled = isSelected && selected.length === 1;

          return (
            <MenuItem
              key={report.id}
              value={report.id}
              disabled={isDisabled}
              data-testid={`report-v2-option-${report.id}`}
              dense
              divider
              disableGutters
            >
              <Checkbox checked={isSelected} disabled={isDisabled} />
              <ListItemText
                primary={`${t("patients.health_reports.q_number_v2", { number: report.week_number })} | ${format(
                  parseISO(report.created_at),
                  "P"
                )}`}
                sx={{ fontSize: 14 }}
              />
            </MenuItem>
          );
        })}
      </StyledSelect>
    </FormControl>
  );
};
